<template>
  <template v-if="translationsToEdit.length > 0">
    <label>{{ $ts('actions.translations') }}</label>
    <RsCollapse
      v-for="lang in translationsToEdit"
      :id="`collapse-language-${lang.languageCode}-${id}`"
      :key="lang.id ?? undefined"
      class="rs-collapse-translations"
      icon="language"
      accordion-card
      not-draggable
      :title="getLanguageName(lang)"
    >
      <template #content>
        <RsFormInput
          v-for="(trns, index) in lang.translations"
          :key="index"
          v-model="trns.value"
          :label="getFieldLabel(trns.key)"
          type="text"
          html-decode
          @input="emitTranslations"
        />
      </template>
    </RsCollapse>
  </template>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { ILanguages } from '@llws/lift-entity-interfaces';
import { cloneObject } from '@/shared';

export default defineComponent({
  name: 'RsCollapseTranslations',

  props: {
    id: {
      type: String,
      required: true,
    },

    value: {
      type: Array as PropType<ILanguages[]>,
      default: () => [],
    },

    /**
     * Vue 3 v-model binding. Kept alongside the legacy `value`/`input` pair so both
     * `v-model` (modelValue/update:modelValue) and explicit `:value`+`@input` consumers
     * work. `undefined` means the parent did not bind v-model (see `boundValue`).
     */
    modelValue: {
      type: Array as PropType<ILanguages[]>,
      default: undefined,
    },
  },

  emits: ['input', 'update:modelValue'],

  data() {
    return {
      translationsToEdit: [] as ILanguages[],
    };
  },

  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): ILanguages[] {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
  },

  watch: {
    boundValue: {
      immediate: true,
      deep: true,
      handler() {
        this.translationsToEdit = cloneObject(this.boundValue);
      },
    },
  },

  methods: {
    emitTranslations(): void {
      this.$emit('input', this.translationsToEdit);
      this.$emit('update:modelValue', this.translationsToEdit);
    },

    getLanguageName(lang: ILanguages): string {
      const code = lang.languageCode as string;
      const key = `languageNames.${code}`;
      const translated = this.$t(key) as string;
      // If i18n found a translation (key !== translation), use it; otherwise fall back to lang.name
      return translated !== key ? translated : (lang.name ?? code);
    },

    getFieldLabel(key: string): string {
      const i18nKey = `translationFieldKeys.${key}`;
      const translated = this.$t(i18nKey) as string;
      return translated !== i18nKey ? translated : this.$utils.capitalize(key.replace('_', ' '));
    },
  },
});
</script>

<style lang="scss">

</style>
